import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowUpRight, Phone, Mail, Clock, ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import BrandUniverse from '../components/BrandUniverse';
import TrustBar from '../components/TrustBar';
import WhatWeProduce from '../components/WhatWeProduce';
import HowItWorks from '../components/HowItWorks';
import FeaturedWork from '../components/FeaturedWork';
import Packages from '../components/Packages';
import Testimonials from '../components/Testimonials';
import Footer from '../components/Footer';
import BookingModal from '../components/BookingModal';
import ContactModal from '../components/ContactModal';
import usePageTracking from '../hooks/usePageTracking';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const heroIndex = [
  { n: '01', name: 'LTV Studio', tag: 'Podcast & Video', dot: 'bg-amber-400', link: 'https://www.ltvstudio.co.uk', external: true },
  { n: '02', name: 'LTV Carnival', tag: 'Carnival Media', dot: 'bg-fuchsia-400', link: 'https://www.ltvcarnival.com', external: true },
  { n: '03', name: 'LTV Live', tag: 'Live Streaming', dot: 'bg-red-500', link: '/live', external: false },
  { n: '04', name: 'LTV On Demand', tag: 'VOD Platform', dot: 'bg-purple-400', link: '/vod', external: false },
];

const Home = () => {
  usePageTracking('home');
  const navigate = useNavigate();
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [blogArticles, setBlogArticles] = useState([]);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await axios.get(`${API}/api/blog/articles?published_only=true`);
        if (res.data && res.data.length > 0) {
          setBlogArticles(res.data);
        }
      } catch (err) {
        console.log('Failed to fetch blog articles');
      }
    };
    fetchArticles();
  }, []);

  const openBookingModal = () => setBookingModalOpen(true);

  const scrollToUniverse = () => {
    document.getElementById('ltv-universe')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white ltv-noise">
      <Navbar />

      {/* ============ HERO ============ */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 sm:pt-28">
        {/* Blueprint grid backdrop, fading out */}
        <div
          className="absolute inset-0 pointer-events-none ltv-grid-bg"
          style={{ maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.9), transparent 80%)', WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.9), transparent 80%)' }}
        />

        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 w-full z-10">
          {/* Top rule with meta */}
          <div className="flex items-center justify-between border-b border-white/[0.08] pb-4 mb-10 sm:mb-14 ltv-reveal">
            <p className="ltv-eyebrow text-gray-500">Production House</p>
            <p className="ltv-eyebrow text-gray-600 hidden md:block">Four Brands · One Umbrella</p>
            <p className="ltv-eyebrow text-red-500">London — Miami — Worldwide</p>
          </div>

          <h1 className="font-display font-bold leading-[0.9] mb-12 sm:mb-16 ltv-reveal ltv-reveal-1">
            <span className="block text-[14vw] sm:text-[11vw] lg:text-[9.5rem] text-white tracking-tight">
              LTV
            </span>
            <span className="block text-[14vw] sm:text-[11vw] lg:text-[9.5rem] tracking-tight">
              <span className="ltv-outline-text">Production</span>
              <span className="text-red-600">.</span>
            </span>
          </h1>

          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-start ltv-reveal ltv-reveal-2">
            {/* Left — statement + CTAs */}
            <div className="lg:col-span-6">
              <p className="text-base sm:text-lg text-gray-400 max-w-xl leading-relaxed mb-10">
                Broadcast-quality content from our studios in London &amp; Miami to any location
                on earth — one production house behind four brands.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openBookingModal}
                  className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 text-white text-base font-semibold px-8 py-4 rounded-full shadow-2xl shadow-red-600/30 transition-all hover:scale-[1.03]"
                >
                  Book a Call
                  <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  onClick={scrollToUniverse}
                  className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/40 text-white text-base font-semibold px-8 py-4 rounded-full transition-all hover:bg-white/[0.05]"
                >
                  Explore the Universe
                  <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Right — brand index */}
            <div className="lg:col-span-5 lg:col-start-8">
              {heroIndex.map((brand) => (
                <button
                  key={brand.n}
                  onClick={() => (brand.external ? window.open(brand.link, '_blank') : navigate(brand.link))}
                  className="group w-full flex items-center gap-5 border-t border-white/[0.08] last:border-b py-4 text-left hover:bg-white/[0.03] transition-colors"
                >
                  <span className="font-display text-xs font-semibold text-gray-600 group-hover:text-red-500 transition-colors w-6">
                    {brand.n}
                  </span>
                  <span className={`w-1.5 h-1.5 rounded-full ${brand.dot}`} />
                  <span className="font-display text-lg sm:text-xl font-bold text-white group-hover:translate-x-1.5 transition-transform duration-300">
                    {brand.name}
                  </span>
                  <span className="ltv-eyebrow text-gray-600 ml-auto hidden sm:block">{brand.tag}</span>
                  <ArrowUpRight className="h-4 w-4 text-gray-700 group-hover:text-white transition-colors" />
                </button>
              ))}
            </div>
          </div>
        </div>

      </section>

      {/* ============ THE LTV UNIVERSE ============ */}
      <BrandUniverse />

      {/* Capability strip */}
      <TrustBar />

      {/* Global presence — visual split */}
      <section className="relative z-10 py-24 sm:py-32 border-t border-white/[0.06]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Photo collage */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-white/[0.08]">
                <img
                  src="https://images.unsplash.com/photo-1530686577637-0ccce382b327?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxNaWFtaSUyMHNreWxpbmUlMjBuaWdodCUyMGFlcmlhbHxlbnwwfHx8fDE3NzU4MTYwMjB8MA&ixlib=rb-4.1.0&q=85"
                  alt="Miami skyline"
                  className="w-full h-[420px] sm:h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-3 sm:-right-6 w-48 sm:w-64 rounded-2xl overflow-hidden border border-white/15 shadow-2xl shadow-black/60">
                <img
                  src="/ltv-studio.jpg"
                  alt="LTV Studio London"
                  className="w-full h-32 sm:h-40 object-cover"
                />
                <span className="absolute bottom-2 left-3 ltv-eyebrow text-white drop-shadow">London Studio</span>
              </div>
            </div>

            {/* Copy */}
            <div>
              <p className="ltv-eyebrow text-red-500 mb-4">02 — Global Presence</p>
              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.05] mb-6">
                London. Miami.
                <span className="text-gray-500"> Anywhere on earth.</span>
              </h2>
              <p className="text-base sm:text-lg text-gray-400 leading-relaxed mb-10">
                Our flagship London studio, our upcoming Miami facility, and an on-location team that brings broadcast-quality production to any corner of the world — from carnival in Trinidad to corporate events in Dubai.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={openBookingModal}
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white text-base font-semibold px-8 py-4 rounded-full shadow-2xl shadow-red-600/30 transition-all hover:scale-[1.03]"
                >
                  Request a Quote
                  <ArrowRight className="h-4 w-4" />
                </button>
                <Link
                  to="/on-location"
                  className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white text-base font-semibold px-8 py-4 rounded-full transition-all hover:bg-white/[0.05]"
                >
                  On-Location Production
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/miami-coming-soon"
                  className="inline-flex items-center gap-2 border border-sky-400/25 hover:border-sky-400/60 text-sky-300 text-base font-semibold px-8 py-4 rounded-full transition-all hover:bg-sky-500/10"
                >
                  Miami — Get Notified
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What we produce */}
      <WhatWeProduce />

      {/* Process */}
      <HowItWorks onBookCall={openBookingModal} />

      {/* Featured work */}
      <FeaturedWork />

      {/* Packages */}
      <Packages onBookCall={openBookingModal} />

      {/* Testimonials */}
      <Testimonials />

      {/* Blog */}
      {blogArticles.length > 0 && (
        <section className="relative z-10 py-24 sm:py-32 border-t border-white/[0.06]">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="ltv-eyebrow text-red-500 mb-4">07 — Journal</p>
                <h2 className="font-display text-4xl sm:text-5xl font-bold text-white">Latest Articles</h2>
                <p className="text-gray-400 mt-3">Insights, guides &amp; stories from the world of streaming</p>
              </div>
              <Link
                to="/vod/blog"
                className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-white border border-white/15 hover:border-white/40 hover:bg-white/[0.05] rounded-full px-5 py-2.5 transition-all"
              >
                View All
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {blogArticles.slice(0, 4).map(article => (
                <Link
                  key={article.id}
                  to={`/vod/blog/${article.slug}`}
                  className="group rounded-2xl overflow-hidden border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20 transition-all duration-500"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800'; }}
                    />
                  </div>
                  <div className="p-5">
                    <span className="ltv-eyebrow text-red-500">{article.category}</span>
                    <h3 className="font-display text-base font-bold mt-2 mb-2 text-white group-hover:text-red-400 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-gray-400 text-sm line-clamp-2 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Clock className="h-3 w-3" />
                      {article.readTime || article.read_time}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-8 text-center sm:hidden">
              <Link
                to="/vod/blog"
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors"
              >
                View All Articles
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* About + FAQ (SEO content) */}
      <section className="relative z-10 py-24 sm:py-32 border-t border-white/[0.06]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-14">
            <div>
              <p className="ltv-eyebrow text-red-500 mb-4">08 — About</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-8">About LTV Production</h2>
              <div className="space-y-4 text-gray-400 leading-relaxed text-sm">
                <p>
                  LTV Production LTD is a UK-based media and entertainment company that provides end-to-end video production, live streaming, and digital content services for creators, brands, and organisations worldwide. Founded with the belief that every story deserves to be told with cinematic quality, we have grown from a small production outfit into a comprehensive media company with studios in London, an upcoming facility in Miami, and on-location production capabilities across the globe.
                </p>
                <p>
                  What sets us apart is our commitment to quality at every level — industry-leading camera systems, professional lighting and audio, and post-production workflows that rival major broadcast networks, combined with the creative storytelling that defines the LTV Production experience.
                </p>
                <p>
                  Beyond production services, LTV Production operates LTV On Demand, our proprietary video-on-demand streaming platform where creators can publish, monetise, and grow their content with industry-leading revenue sharing. We also run LTV Carnival, our dedicated carnival media division that provides comprehensive coverage of Caribbean carnival events worldwide, from Trinidad and Tobago Carnival to Notting Hill Carnival and beyond. Together, these divisions make LTV Production a true one-stop shop for content creation, distribution, and monetisation.
                </p>
              </div>
            </div>

            <div>
              <p className="ltv-eyebrow text-red-500 mb-4">09 — FAQ</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-8">Frequently Asked Questions</h2>
              <div className="space-y-3">
                {[
                  {
                    q: "What production services does LTV Production offer?",
                    a: "We offer a comprehensive range of production services including podcast recording, video production, live streaming, documentary filmmaking, corporate and brand content, music video production, social media content creation, event coverage, and carnival media packages. Our services are available both in-studio and on-location worldwide."
                  },
                  {
                    q: "Where are your studios located?",
                    a: "Our flagship studio is in London, UK, fully equipped for podcast recording, video shoots, and interviews. We are also opening a second studio in Miami, USA, bringing the same premium production experience to the American market. Additionally, our on-location team can travel anywhere in the world for your production needs."
                  },
                  {
                    q: "How much does it cost to book a production?",
                    a: "Our pricing varies depending on the scope and complexity of your project. We offer a range of packages from half-day studio sessions to multi-day on-location productions. The best way to get an accurate quote is to book a call with our team, where we can discuss your specific requirements and recommend the right package for your budget and goals."
                  },
                  {
                    q: "What is LTV On Demand and how can I get my content on it?",
                    a: "LTV On Demand is our video-on-demand streaming platform where creators can publish and monetise their content. We offer competitive revenue sharing, professional hosting, and built-in audience growth tools. If you are interested in distributing your content through LTV On Demand, visit our Creator Package page or contact our team directly."
                  },
                  {
                    q: "Do you offer live streaming services?",
                    a: "Yes, live streaming is one of our core services. We provide professional multi-camera live streaming for events, concerts, conferences, and special occasions. Our live streams include real-time graphics, audience interaction features, and can be broadcast to multiple platforms simultaneously. We handle all technical aspects so you can focus on your event."
                  },
                  {
                    q: "Can you travel to my location for filming?",
                    a: "Absolutely. Our on-location production team is available worldwide. We bring all necessary equipment including cameras, lighting, audio gear, and production staff to your location. We have experience filming across the UK, Caribbean, Europe, North America, and beyond. Contact us to discuss your on-location production needs."
                  },
                ].map((faq, i) => (
                  <details key={i} className="group border border-white/[0.08] rounded-xl bg-white/[0.02]">
                    <summary className="flex items-center justify-between cursor-pointer px-5 py-4 text-sm font-medium text-white hover:bg-white/[0.04] rounded-xl">
                      {faq.q}
                      <ChevronRight className="h-4 w-4 text-gray-500 group-open:rotate-90 transition-transform flex-shrink-0 ml-2" />
                    </summary>
                    <p className="px-5 pb-4 text-sm text-gray-400 leading-relaxed">{faq.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="final-cta" className="relative z-10 py-28 sm:py-40 border-t border-white/[0.06] overflow-hidden">
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 text-center">
          <p className="ltv-eyebrow text-red-500 mb-6">Let's Create</p>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl font-bold text-white leading-[1.0] mb-8">
            Ready to create
            <br />
            <span className="bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent">something powerful?</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-400 mb-12 max-w-xl mx-auto leading-relaxed">
            Cinematic visuals, clean sound and premium storytelling — let's bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={openBookingModal}
              className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 text-white text-base font-semibold px-9 py-4 rounded-full shadow-2xl shadow-red-600/30 transition-all hover:scale-[1.03]"
            >
              Book a Call
              <Phone className="h-4 w-4" />
            </button>
            <button
              onClick={openBookingModal}
              className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/40 text-white text-base font-semibold px-9 py-4 rounded-full transition-all hover:bg-white/[0.05]"
            >
              Request a Quote
              <Mail className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      <Footer />

      <BookingModal isOpen={bookingModalOpen} onClose={() => setBookingModalOpen(false)} />
      <ContactModal isOpen={contactModalOpen} onClose={() => setContactModalOpen(false)} />
    </div>
  );
};

export default Home;
